import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllPDFForms, getPDFFormsCountByType, deletePDFForm, downloadPDF } from './supabaseService';

const styles = {
  container: {
    maxWidth: 1200,
    margin: '40px auto',
    padding: '24px',
    background: '#f6f7fb',
    borderRadius: '18px',
    minHeight: '100vh',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '2.5rem',
    fontWeight: '900',
    color: '#c00',
    marginBottom: '16px',
  },
  subtitle: {
    fontSize: '1.2rem',
    color: '#666',
    marginBottom: '32px',
  },
  searchSection: {
    background: '#fff',
    padding: '24px',
    borderRadius: '12px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  searchInput: {
    width: '100%',
    padding: '16px',
    fontSize: '18px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    marginBottom: '16px',
  },
  filterSection: {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  dateInput: {
    padding: '12px',
    border: '2px solid #ddd',
    borderRadius: '8px',
    fontSize: '16px',
  },
  filterButtons: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  filterButton: {
    padding: '8px 16px',
    border: '2px solid #c00',
    borderRadius: '20px',
    background: 'transparent',
    color: '#c00',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
  },
  activeFilter: {
    background: '#c00',
    color: '#fff',
  },
  statsSection: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    background: '#fff',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  statNumber: {
    fontSize: '2rem',
    fontWeight: '900',
    color: '#c00',
    marginBottom: '8px',
  },
  statLabel: {
    fontSize: '14px',
    color: '#666',
    fontWeight: '600',
  },
  pdfsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px',
    marginTop: '24px',
  },
  pdfCard: {
    background: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    border: '1px solid #eee',
    transition: 'transform 0.2s, box-shadow 0.2s',
  },
  pdfCardHover: {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
  },
  pdfType: {
    background: '#c00',
    color: '#fff',
    padding: '4px 12px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-block',
    marginBottom: '12px',
  },
  pdfTitle: {
    fontSize: '18px',
    fontWeight: '700',
    color: '#333',
    marginBottom: '8px',
  },
  pdfMeta: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '8px',
  },
  pdfActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  },
  actionButton: {
    padding: '8px 16px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    flex: 1,
  },
  downloadButton: {
    background: '#28a745',
    color: '#fff',
  },
  deleteButton: {
    background: '#dc3545',
    color: '#fff',
  },
  backButton: {
    background: '#6c757d',
    color: '#fff',
    padding: '12px 24px',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: '24px',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#666',
  },
  loading: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  fileSize: {
    background: '#f8f9fa',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    color: '#666',
    display: 'inline-block',
    marginLeft: '8px',
  }
};

const PDFDashboard = () => {
  const [pdfs, setPdfs] = useState([]);
  const [filteredPdfs, setFilteredPdfs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    loadPDFs();
  }, []);

  useEffect(() => {
    filterPDFs();
  }, [searchTerm, activeFilter, startDate, endDate, pdfs]);

  const loadPDFs = async () => {
    try {
      setLoading(true);
      const allPDFs = await getAllPDFForms();
      const countByType = await getPDFFormsCountByType();
      
      console.log('Raw PDF data from database:', allPDFs);
      setPdfs(allPDFs);
      setStats(countByType);
    } catch (error) {
      console.error('Error loading PDFs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPDFs = () => {
    let filtered = pdfs;

    // Apply type filter
    if (activeFilter !== 'all') {
      filtered = filtered.filter(pdf => pdf.form_type === activeFilter);
    }

    // Apply date range filter
    if (startDate && endDate) {
      const start = new Date(startDate);
      const end = new Date(endDate);
      filtered = filtered.filter(pdf => {
        const pdfDate = new Date(pdf.date || pdf.created_at);
        return pdfDate >= start && pdfDate <= end;
      });
    }

    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(pdf => 
        pdf.title?.toLowerCase().includes(term) ||
        pdf.form_type?.toLowerCase().includes(term) ||
        pdf.location?.toLowerCase().includes(term) ||
        pdf.created_by?.toLowerCase().includes(term) ||
        pdf.file_name?.toLowerCase().includes(term)
      );
    }

    setFilteredPdfs(filtered);
  };

  const handleDeletePDF = async (pdfId) => {
    if (window.confirm('Are you sure you want to delete this PDF? This action cannot be undone.')) {
      try {
        await deletePDFForm(pdfId, pdfs.find(pdf => pdf.id === pdfId)?.file_name);
        await loadPDFs(); // Reload PDFs
      } catch (error) {
        console.error('Error deleting PDF:', error);
        alert('Error deleting PDF. Please try again.');
      }
    }
  };

  const handleDownloadPDF = async (pdfId) => {
    try {
              await downloadPDF(pdfId);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Error downloading PDF. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'No date';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid date';
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 Bytes';
    if (isNaN(bytes)) return 'Unknown size';
    
    try {
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    } catch (error) {
      console.error('Error formatting file size:', bytes, error);
      return 'Unknown size';
    }
  };

  const getFormTypeLabel = (formType) => {
    const labels = {
      'waste-transfer': 'Waste Transfer',
      'quality-review': 'Quality Review',
      'surgery-audit': 'Surgery Audit',
      'fettler': 'Fettler',
      'eec': 'EEC',
      'hwmoon': 'HW Moon Toyota',
      'vwcorkill': 'VW Corkill',
      'kensite': 'Ken Site',
      'form1': 'Form 1',
      'form2': 'Form 2',
      'form3': 'Form 3'
    };
    return labels[formType] || formType;
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading PDFs...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <button 
        style={styles.backButton}
        onClick={() => navigate('/')}
      >
        ← Back to Home
      </button>

      <div style={styles.header}>
        <h1 style={styles.title}>PDF Forms Library</h1>
        <p style={styles.subtitle}>
          Search, view, and manage all your saved PDF forms
        </p>
      </div>

      {/* Statistics */}
      <div style={styles.statsSection}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{pdfs.length}</div>
          <div style={styles.statLabel}>Total PDFs</div>
        </div>
        {Object.entries(stats).map(([type, count]) => (
          <div key={type} style={styles.statCard}>
            <div style={styles.statNumber}>{count}</div>
            <div style={styles.statLabel}>{getFormTypeLabel(type)}</div>
          </div>
        ))}
      </div>

      <div style={styles.searchSection}>
        <input
          type="text"
          placeholder="Search PDFs by title, type, location, creator, or filename..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
        
        <div style={styles.filterSection}>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={styles.dateInput}
            placeholder="Start Date"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            style={styles.dateInput}
            placeholder="End Date"
          />
        </div>
        
        <div style={styles.filterButtons}>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'all' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('all')}
          >
            All PDFs ({pdfs.length})
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'waste-transfer' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('waste-transfer')}
          >
            Waste Transfer
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'quality-review' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('quality-review')}
          >
            Quality Review
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'surgery-audit' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('surgery-audit')}
          >
            Surgery Audit
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'fettler' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('fettler')}
          >
            Fettler
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'eec' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('eec')}
          >
            EEC
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'hwmoon' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('hwmoon')}
          >
            HW Moon Toyota
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'vwcorkill' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('vwcorkill')}
          >
            VW Corkill
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'kensite' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('kensite')}
          >
            Ken Site
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'form1' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('form1')}
          >
            Form 1
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'form2' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('form2')}
          >
            Form 2
          </button>
          <button
            style={{
              ...styles.filterButton,
              ...(activeFilter === 'form3' && styles.activeFilter)
            }}
            onClick={() => setActiveFilter('form3')}
          >
            Form 3
          </button>
        </div>
      </div>

      {filteredPdfs.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>No PDFs found</h3>
          <p>
            {searchTerm || activeFilter !== 'all' || startDate || endDate
              ? 'Try adjusting your search, filters, or date range'
              : 'Start by creating your first PDF form!'
            }
          </p>
        </div>
      ) : (
        <div style={styles.pdfsGrid}>
          {filteredPdfs.map((pdf) => (
            <div key={pdf.id} style={styles.pdfCard}>
              <div style={styles.pdfType}>
                {getFormTypeLabel(pdf.form_type)}
              </div>
              <h3 style={styles.pdfTitle}>
                {pdf.title || `${getFormTypeLabel(pdf.form_type)} Form`}
              </h3>
              <div style={styles.pdfMeta}>
                <div>Created: {formatDate(pdf.date || pdf.created_at)}</div>
                <div>
                  File: {pdf.file_name}
                  <span style={styles.fileSize}>
                    {formatFileSize(pdf.file_size)}
                  </span>
                </div>
              </div>
              <div style={styles.pdfActions}>
                <button
                  style={{...styles.actionButton, ...styles.downloadButton}}
                  onClick={() => handleDownloadPDF(pdf.id)}
                >
                  Download PDF
                </button>
                <button
                  style={{...styles.actionButton, ...styles.deleteButton}}
                  onClick={() => handleDeletePDF(pdf.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PDFDashboard;
